import type { HeartbeatObservationKind } from './types'

/**
 * The pure calculations behind the delivery, transcript and heartbeat checks.
 * Every function takes the time it needs as an argument; none of them reads a
 * clock, an observable, or a room. This is the only place the rules live — room
 * listeners, React components and transcript storage hold no copies.
 */

/** A local speech interval. `end` is null while the person is still speaking. */
export type SpeechInterval = { start: number; end: number | null }

/**
 * Total local speech inside the last window, counting speech in progress. Speech
 * in progress counts because a student who talks without pausing produced no
 * evidence under the old check, and neither does the E2E fake microphone, a
 * continuous tone.
 */
export function totalSpeechInWindow(
  intervals: SpeechInterval[],
  windowStart: number,
  now: number
): number {
  let total = 0
  for (const interval of intervals) {
    const start = Math.max(interval.start, windowStart)
    const end = Math.min(interval.end ?? now, now)
    if (end > start) total += end - start
  }
  return total
}

/**
 * The earliest speech start that still has time inside the window, or null when
 * no interval overlaps it.
 */
export function firstSpeechStartInWindow(
  intervals: SpeechInterval[],
  windowStart: number,
  now: number
): number | null {
  let earliest: number | null = null
  for (const interval of intervals) {
    const end = interval.end ?? now
    if (end <= windowStart) continue
    const start = Math.max(interval.start, windowStart)
    if (start >= now) continue
    if (earliest === null || start < earliest) earliest = start
  }
  return earliest
}

/**
 * Confirm a delivery failure when meaningful local speech in the window has no
 * server confirmation after the confirmation delay. Own transcript progress in
 * the same window prevents a contradictory delivery warning: if the words are
 * being transcribed, they are reaching the session.
 */
export function shouldConfirmDelivery(params: {
  speechMS: number
  firstSpeechAt: number | null
  now: number
  serverConfirmedInWindow: boolean
  ownTranscriptInWindow: boolean
  minSpeechMS: number
  delayMS: number
}): boolean {
  const {
    speechMS,
    firstSpeechAt,
    now,
    serverConfirmedInWindow,
    ownTranscriptInWindow,
    minSpeechMS,
    delayMS,
  } = params
  if (firstSpeechAt === null) return false
  if (serverConfirmedInWindow) return false
  if (ownTranscriptInWindow) return false
  if (speechMS < minSpeechMS) return false
  if (now - firstSpeechAt < delayMS) return false
  return true
}

/**
 * Add the elapsed time under the previous input state before an input change is
 * applied, so a delayed tick does not miscount. `eligibleSince` is null when the
 * accumulator was not running.
 */
export function accrueElapsed(
  accumulatedMS: number,
  eligibleSince: number | null,
  now: number
): number {
  if (eligibleSince === null) return accumulatedMS
  return accumulatedMS + Math.max(0, now - eligibleSince)
}

/** A stored transcript-flow deadline has passed. */
export function transcriptWaitExpired(
  deadline: number | null,
  now: number
): boolean {
  return deadline !== null && now >= deadline
}

/** The solo safeguard has accumulated enough eligible discussion time. */
export function soloSafeguardTripped(
  accumulatedMS: number,
  limitMS: number
): boolean {
  return accumulatedMS >= limitMS
}

/** The evidence a heartbeat observation is derived from. */
export type HeartbeatState = {
  // The Secretary participant is in the room.
  secretaryPresent: boolean
  // When a fresh status for the current run last arrived, local time.
  lastStatusAt: number | null
  // When the current microphone SID last appeared in a status row.
  lastTrackRowAt: number | null
  // When the current track's packet counter last advanced.
  lastPacketAdvanceAt: number | null
  // When the current track's audible-frame counter last advanced.
  lastAudibleAdvanceAt: number | null
  // Whether audible frames are being measured at all (framesAboveGate present).
  audibleMeasured: boolean
  // Decode errors observed since the previous snapshot.
  decodeErrorsDelta: number
  // Local speech has been observed over the reachability window, so packets and
  // audible frames are expected to advance.
  expectingSpeech: boolean
}

/**
 * The single heartbeat observation that best explains the current listening
 * state, or null when the Secretary is listening normally. These explain
 * problems in telemetry; they open no dialog. Missing status is unknown health,
 * not proof that transcription failed.
 */
export function heartbeatObservation(
  state: HeartbeatState,
  now: number,
  timeoutMS: number,
  reachabilityWindowMS: number
): HeartbeatObservationKind | null {
  const statusFresh =
    state.lastStatusAt !== null && now - state.lastStatusAt <= timeoutMS
  if (!statusFresh) return 'no_heartbeat'

  // Decode errors are their own signal regardless of the listening state.
  if (state.decodeErrorsDelta > 0) return 'decode_errors'

  const trackListed =
    state.lastTrackRowAt !== null && now - state.lastTrackRowAt <= timeoutMS
  if (!trackListed) return 'not_listening'

  if (!state.expectingSpeech) return null

  const packetsAdvancing =
    state.lastPacketAdvanceAt !== null &&
    now - state.lastPacketAdvanceAt <= timeoutMS
  if (!packetsAdvancing) return 'no_packets'

  if (state.audibleMeasured) {
    const audibleAdvancing =
      state.lastAudibleAdvanceAt !== null &&
      now - state.lastAudibleAdvanceAt <= reachabilityWindowMS
    if (!audibleAdvancing) return 'no_audible_frames'
  }

  return null
}
